import React, { useCallback, useMemo, useState } from "react";

import { Stack } from "@mui/material";
import { useParams } from "react-router-dom";

import useGetFartherManagedAccounts from "@fartherfinance/frontend/api/Accounts/hooks/useGetFartherManagedAccounts";
import useFindPortfolioPlanAssignment from "@fartherfinance/frontend/api/Investment/hooks/useFindPortfolioPlanAssignment";
import useGetPortfolioPlanAssignments from "@fartherfinance/frontend/api/Investment/hooks/usePortfolioPlanAssignments";
import usePostPortfolioAssignment from "@fartherfinance/frontend/api/Investment/hooks/usePostPortfolioAssignment";
import {
  ClientId,
  FartherAccountId,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import { TAX_CHARACTERISTICS_ACROSS_ACCOUNTS_TOOLTIP_TEXT } from "@src/multiCustodian/components/Client/Portfolio/const";
import CreateTradingGroupV2, {
  ErrorCreateTradingTable,
  LoadingCreateTradingTable,
  SelectableAccountRowV2Props,
} from "@src/multiCustodian/components/Client/Portfolio/TradingGroupsTable/CreateTradingGroupTableV2";
import TradingTableGroupsTableV2, {
  ErrorTradingTableGroups,
  TradingTableGroupsData,
} from "@src/multiCustodian/components/Client/Portfolio/TradingGroupsTable/TradingGroupsTableV2";
import LoadingTradingTableGroups from "@src/multiCustodian/components/Client/Portfolio/TradingGroupsTable/TradingGroupsTableV2Loading";
import BaseDrawer from "@src/multiCustodian/components/Client/Portfolio/v2/ModelDrawer/BaseDrawer";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import TooltipBasic from "@src/sharedComponents/Tooltip/TooltipBasic";
import Text from "@src/sharedComponents/Typography/Text";

const PortfoliosV2Accounts: React.FC<React.PropsWithChildren<object>> = ({
  children,
}) => {
  const { portfolioPlanId, clientId } = useParams<{
    portfolioPlanId: PortfolioPlanId;
    clientId: ClientId;
  }>();

  return (
    <BaseDrawer>
      <TradingTableGroupsTable
        clientId={clientId}
        portfolioPlanId={portfolioPlanId}
      />
      <CreateTradingGroupsTable
        clientId={clientId}
        portfolioPlanId={portfolioPlanId}
      />
      {children}
    </BaseDrawer>
  );
};

export default PortfoliosV2Accounts;

type TradingTableProps = {
  clientId: ClientId;
  portfolioPlanId: PortfolioPlanId;
};

const TradingTableGroupsTable = ({
  clientId,
  portfolioPlanId,
}: TradingTableProps) => {
  const auth = useRequestAuth();
  const managedAccounts = useGetFartherManagedAccounts(clientId, auth);

  // use get portfolio assignments is being able to fetch all the assignments for a portfolio plan
  const portfolioAssignments = useGetPortfolioPlanAssignments(
    {
      abstractPortfolioId: portfolioPlanId,
      queryString: { accountOwnedBy: clientId },
    },
    auth
  );

  // extract account data from managedAccounts to reference each account in portfolioAssignments
  const groupAssignments: TradingTableGroupsData[] = useMemo(() => {
    if (
      portfolioAssignments.data === undefined ||
      managedAccounts.data === undefined
    ) {
      return [];
    }

    // for all the my assignments in the portfolio plan
    return portfolioAssignments.data.data.map((assignment) => {
      const accounts = assignment.accounts.map((account) => {
        const foundManagedAccount = managedAccounts.data.find(
          (managedAccount) => managedAccount.virtualAccountId === account
        );

        if (foundManagedAccount === undefined) {
          throw new Error(
            `Managed account ${account} not found for assignment ${assignment.planAssignmentId}`
          );
        }

        const accountName = `${
          foundManagedAccount.accountDetails.displayName
        } - ${
          foundManagedAccount.accountDetails.custodianAccountNumber ?? "N/A"
        }`;

        return {
          fartherAccountId: account,
          accountName,
        };
      });

      return {
        tradingGroupName: assignment.name,
        assignmentId: assignment.planAssignmentId,
        accounts,
        intent: assignment.intent,
        status: assignment.status,
        transformers: assignment.transformers.map(
          (transformer) => transformer.transformerId
        ),
        rebalancers: assignment.rebalancers
          .map((rebalancer) => rebalancer.rebalancer)
          .filter((rebalancer) => rebalancer !== "LiquidationRebalancer"),
      };
    });
  }, [managedAccounts.data, portfolioAssignments.data]);

  return (
    <React.Fragment>
      <Stack direction={"row"} paddingBottom={2}>
        <Text>Trading Groups</Text>
        <TooltipBasic
          type={"primary"}
          text={TAX_CHARACTERISTICS_ACROSS_ACCOUNTS_TOOLTIP_TEXT}
        />
      </Stack>
      {portfolioAssignments.isLoading ? (
        <LoadingTradingTableGroups />
      ) : portfolioAssignments.hasError ? (
        <ErrorTradingTableGroups />
      ) : (
        <>
          {groupAssignments.length === 0 ? (
            <Text
              style={{ paddingBottom: 32 }}
              color="textSubtle"
              variant="body"
            >
              No active trading groups
            </Text>
          ) : (
            groupAssignments.map((account, index) => (
              <TradingTableGroupsTableV2
                key={index}
                accounts={account.accounts}
                status={account.status}
                intent={account.intent}
                rebalancers={account.rebalancers}
                assignmentId={account.assignmentId}
                tradingGroupName={account.tradingGroupName}
                transformers={account.transformers}
                portfolioPlanId={portfolioPlanId}
                clientId={clientId}
              />
            ))
          )}
        </>
      )}
    </React.Fragment>
  );
};

type CreateTradingGroupsTableProps = {
  clientId: ClientId;
  portfolioPlanId: PortfolioPlanId;
};

function CreateTradingGroupsTable({
  clientId,
  portfolioPlanId,
}: CreateTradingGroupsTableProps) {
  const [loading, setLoading] = useState(false);

  const auth = useRequestAuth();
  const accounts = useGetFartherManagedAccounts(clientId, auth);
  const notify = useStatusNotification();

  const { mutateAsync: postPortfolioAssignment } = usePostPortfolioAssignment(
    portfolioPlanId,
    auth
  );

  const allAssignmentsPerPortfolioPlan = useFindPortfolioPlanAssignment(
    { clientId },
    {
      page: 1,
      pageSize: 100,
      orderBy: "createdAt asc",
    },
    auth
  );

  const groupAssignments: SelectableAccountRowV2Props[] = useMemo(() => {
    const foundAssignment = allAssignmentsPerPortfolioPlan.data?.data;

    if (accounts.data === undefined || foundAssignment === undefined) {
      return [];
    }

    return accounts.data
      .filter((account) => account.accountOwner.includes(clientId)) // Filter out shared-to accounts
      .map((account) => {
        if (account.accountDetails.custodianAccountNumber === null) {
          throw new Error("custodianAccountNumber should not be null");
        }

        const accountName = `${account.accountDetails.displayName} - ${account.accountDetails.custodianAccountNumber}`;

        // check on each to see if account is attached to that CIP
        const cipMatchingAccount = foundAssignment.find((cip) =>
          cip.accounts.includes(account.virtualAccountId)
        );

        return {
          accountBalance: account.accountDetails.lastReportedBalance.balance,
          accountName,
          custodianAccountNumber: account.virtualAccountId,
          isJointAccount: account.accountOwner.length > 1,
          fartherAccountId: account.virtualAccountId,
          isDisabled: cipMatchingAccount !== undefined,
          portfolioPlanId: cipMatchingAccount?.portfolioPlanId || null,
          disabledReason: null,
          clientId,
        };
      });
  }, [accounts.data, allAssignmentsPerPortfolioPlan.data?.data, clientId]);

  const batchAccounts = useCallback(
    async (selectedAccounts: FartherAccountId[], isIndividual: boolean) => {
      setLoading(true);

      try {
        if (!isIndividual) {
          await postPortfolioAssignment({
            name: "Trading Group",
            intent: "TradingOn",
            transformers: [],
            rebalancers: [{ rebalancer: "AlphaRebalancer" }],
            virtualAccountIds: selectedAccounts,
          });
        } else {
          const separateCalls = selectedAccounts.map((accountId) =>
            postPortfolioAssignment({
              name: "Trading Group",
              intent: "TradingOn",
              transformers: [],
              rebalancers: [{ rebalancer: "AlphaRebalancer" }],
              virtualAccountIds: [accountId],
            })
          );

          await Promise.all(separateCalls);
        }
        notify("Trading group(s) created successfully", "Success");
      } catch {
        notify("Error creating trading group", "Error");
      } finally {
        setLoading(false);
      }
    },
    [notify, postPortfolioAssignment]
  );

  return (
    <React.Fragment>
      {accounts.isLoading || loading ? (
        <LoadingCreateTradingTable />
      ) : accounts.hasError ? (
        <ErrorCreateTradingTable />
      ) : (
        <CreateTradingGroupV2 data={groupAssignments} onApply={batchAccounts} />
      )}
    </React.Fragment>
  );
}
